// src/pages/HR/reports/RecruitmentOnboarding.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc } from "firebase/firestore";
import { db, storage } from "../../../services/firebase";
import { ref as sRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import HRCampusSelector from "../../../components/HRCampusSelector";

function exportCSV(filename, rows) {
  const header = ["Candidate","Position","Stage","Start Date","Campus","Source","Contact","Remarks"];
  const csv = [header, ...rows.map(r => [r.candidateName, r.position, r.stage, r.startDate || "", r.campus, r.source || "", r.contact || "", r.remarks || ""])]
    .map(r => r.map(x => `"${String(x || "").replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}
function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length);
  const out = [];
  for (const line of lines) {
    const cells = [];
    let cur = "", inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQ && line[i+1] === '"') { cur += '"'; i++; } else inQ = !inQ;
      } else if (ch === "," && !inQ) { cells.push(cur); cur = ""; }
      else cur += ch;
    }
    cells.push(cur);
    out.push(cells);
  }
  return out;
}
function getField(obj, aliases) {
  for (const n of aliases) if (Object.prototype.hasOwnProperty.call(obj, n)) return obj[n];
  return undefined;
}

export default function RecruitmentOnboarding() {
  const [campus, setCampus] = useState(() => localStorage.getItem("hr_reports_campus") || "bulacan");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ candidateName: "", position: "", stage: "Screening", startDate: "", source: "", contact: "", remarks: "" });
  const [taskOpen, setTaskOpen] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskForm, setTaskForm] = useState({ task: "", owner: "HR", dueDate: "", notes: "" });
  const [filesOpen, setFilesOpen] = useState(null);
  const [filesList, setFilesList] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "hr_candidates"));
      setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      try {
        const es = await getDocs(collection(db, "employees"));
        setEmployees(es.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { void e; }
    })();
  }, []);

  const scoped = useMemo(() => rows.filter(r => String(r.campus || "") === campus), [rows, campus]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return scoped;
    return scoped.filter(r => [r.candidateName, r.position, r.stage, r.source, r.contact].some(x => String(x || "").toLowerCase().includes(s)));
  }, [scoped, q]);

  async function reload() {
    const snap = await getDocs(collection(db, "hr_candidates"));
    setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }
  async function importCSV(file) {
    const text = await file.text();
    const lines = parseCSV(text);
    if (lines.length < 2) { alert("CSV has no data"); return; }
    const headers = lines[0].map(h => String(h).trim().toLowerCase());
    const exSnap = await getDocs(collection(db, "hr_candidates"));
    const existing = exSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const idx = new Map(existing.map(r => [`${String(r.candidateName||"").toLowerCase()}|${r.campus}`, r.id]));
    let updated = 0, created = 0;
    for (let i = 1; i < lines.length; i++) {
      const obj = {}; lines[i].forEach((v,j)=>obj[headers[j]] = v);
      const candidateName = getField(obj, ["candidate","candidate name","candidate_name"]) || "";
      const position = getField(obj, ["position","role"]) || "";
      const stage = getField(obj, ["stage","status"]) || "Screening";
      const startDate = getField(obj, ["start date","start_date","startdate"]) || "";
      const source = getField(obj, ["source"]) || "";
      const contact = getField(obj, ["contact","email","phone"]) || "";
      const remarks = getField(obj, ["remarks","notes"]) || "";
      const rec = { candidateName, position, stage, startDate, campus, source, contact, remarks, updatedAt: new Date().toISOString() };
      const key = `${String(candidateName).toLowerCase()}|${campus}`;
      const found = idx.get(key);
      if (found) { await updateDoc(doc(db, "hr_candidates", found), rec); updated++; }
      else { await addDoc(collection(db, "hr_candidates"), { ...rec, createdAt: new Date().toISOString() }); created++; }
    }
    await reload();
    alert(`Import complete — Updated: ${updated}, Created: ${created}.`);
  }
  async function saveCandidate(e) {
    e.preventDefault();
    const rec = { ...form, campus, createdAt: new Date().toISOString() };
    await addDoc(collection(db, "hr_candidates"), rec);
    setShowAdd(false);
    await reload();
  }
  async function updateStage(row, newStage) {
    await updateDoc(doc(db, "hr_candidates", row.id), { stage: newStage, updatedAt: new Date().toISOString() });
    if (newStage === "Hired" && !row.startDate) {
      // default start date to next working day if not set
      const d = new Date(); d.setDate(d.getDate()+1);
      await updateDoc(doc(db, "hr_candidates", row.id), { startDate: d.toISOString().slice(0,10) });
    }
    await reload();
  }
  async function createEmployee(row) {
    const payload = {
      name: row.candidateName,
      position: row.position || "",
      department: "",
      salary: 0,
      email: row.contact || "",
      phone: "",
      campus: campus,
      createdAt: new Date().toISOString()
    };
    await addDoc(collection(db, "employees"), payload);
    alert("Employee record created.");
  }
  const DEFAULT_TASKS = [
    { task: "Collect pre-employment documents", owner: "HR" },
    { task: "Create email and system accounts", owner: "IT" },
    { task: "Issue ID and uniforms", owner: "Admin" },
    { task: "Orientation with department head", owner: "HR" },
  ];
  async function seedTasks(row) {
    for (const t of DEFAULT_TASKS) {
      await addDoc(collection(db, "hr_onboarding_tasks"), {
        candidateId: row.id,
        candidateName: row.candidateName,
        campus,
        task: t.task,
        owner: t.owner,
        dueDate: row.startDate || "",
        status: "Open",
        notes: "",
        createdAt: new Date().toISOString()
      });
    }
    alert("Onboarding checklist seeded.");
    if (taskOpen && taskOpen.id === row.id) await openTasks(row);
  }
  async function openTasks(row) {
    setTaskOpen(row);
    const snap = await getDocs(collection(db, "hr_onboarding_tasks"));
    setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.campus === campus && t.candidateId === row.id));
  }
  async function addTask(e) {
    e.preventDefault();
    if (!taskOpen) return;
    await addDoc(collection(db, "hr_onboarding_tasks"), {
      candidateId: taskOpen.id,
      candidateName: taskOpen.candidateName,
      campus,
      task: taskForm.task,
      owner: taskForm.owner,
      dueDate: taskForm.dueDate,
      status: "Open",
      notes: taskForm.notes || "",
      createdAt: new Date().toISOString()
    });
    setTaskForm({ task: "", owner: "HR", dueDate: "", notes: "" });
    await openTasks(taskOpen);
  }
  async function setTaskStatus(t, status) {
    await updateDoc(doc(db, "hr_onboarding_tasks", t.id), { status, updatedAt: new Date().toISOString() });
    await openTasks(taskOpen);
  }
  async function removeCandidate(row) {
    if (!window.confirm("Delete this candidate?")) return;
    await deleteDoc(doc(db, "hr_candidates", row.id));
    await reload();
  }

  async function openFiles(row) {
    setFilesOpen(row);
    const snap = await getDoc(doc(db, "hr_candidates", row.id));
    const data = snap.exists() ? snap.data() : {};
    setFilesList(Array.isArray(data.attachments) ? data.attachments : []);
  }
  async function uploadFile(e) {
    if (!filesOpen) return;
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = `hr_candidates/${filesOpen.id}/${Date.now()}-${file.name}`;
      const r = sRef(storage, path);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      const item = { name: file.name, url, path, type: file.type, uploadedAt: new Date().toISOString() };
      const snap = await getDoc(doc(db, "hr_candidates", filesOpen.id));
      const data = snap.exists() ? snap.data() : {};
      const attachments = Array.isArray(data.attachments) ? [...data.attachments, item] : [item];
      await updateDoc(doc(db, "hr_candidates", filesOpen.id), { attachments, updatedAt: new Date().toISOString() });
      setFilesList(attachments);
      e.target.value = "";
    } finally {
      setUploading(false);
    }
  }
  async function removeFile(item) {
    if (!filesOpen) return;
    const ok = window.confirm(`Remove file ${item.name}?`);
    if (!ok) return;
    try { if (item.path) await deleteObject(sRef(storage, item.path)); } catch (e) { /* ignore */ }
    const snap = await getDoc(doc(db, "hr_candidates", filesOpen.id));
    const data = snap.exists() ? snap.data() : {};
    const attachments = (Array.isArray(data.attachments) ? data.attachments : []).filter(x => x.url !== item.url);
    await updateDoc(doc(db, "hr_candidates", filesOpen.id), { attachments, updatedAt: new Date().toISOString() });
    setFilesList(attachments);
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Recruitment & Onboarding</h3>
        <div className="d-flex align-items-center gap-2 print-hide">
          <HRCampusSelector selectedCampus={campus} onChange={setCampus} />
          <input className="form-control" style={{ width: 260 }} placeholder="Search candidate/position/status" value={q} onChange={e => setQ(e.target.value)} />
          <input type="file" accept=".csv" className="form-control" onChange={e => e.target.files?.[0] && importCSV(e.target.files[0])} />
          <button className="btn btn-outline-secondary" onClick={() => window.print()}>Print</button>
          <button className="btn btn-primary" onClick={() => exportCSV(`recruitment-onboarding-${campus}.csv`, filtered)}>Export CSV</button>
          <button className="btn btn-success" onClick={() => setShowAdd(true)}>Add Candidate</button>
        </div>
      </div>
      <p className="text-muted">Track candidates from screening to hire and manage onboarding tasks.</p>
      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead className="table-light">
            <tr>
              <th>Candidate</th>
              <th>Position</th>
              <th>Stage</th>
              <th>Start Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="5" className="text-center text-muted">No records</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id}>
                <td>{r.candidateName}</td>
                <td>{r.position}</td>
                <td style={{ minWidth: 160 }}>
                  <select className="form-select form-select-sm" value={r.stage || "Screening"} onChange={e => updateStage(r, e.target.value)}>
                    <option>Screening</option>
                    <option>Interview</option>
                    <option>Offer</option>
                    <option>Hired</option>
                    <option>Declined</option>
                  </select>
                </td>
                <td>{r.startDate || "-"}</td>
                <td className="d-flex gap-2">
                  <button className="btn btn-sm btn-outline-secondary" onClick={() => openTasks(r)}>Tasks</button>
                  <button className="btn btn-sm btn-outline-success" onClick={() => createEmployee(r)} disabled={(r.stage || "") !== "Hired"}>Create Employee</button>
                  <button className="btn btn-sm btn-outline-primary" onClick={() => seedTasks(r)} disabled={(r.stage || "") !== "Hired"}>Seed Checklist</button>
                <button className="btn btn-sm btn-outline-warning" onClick={() => openFiles(r)}>Files</button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => removeCandidate(r)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: "rgba(0,0,0,0.35)", zIndex: 1050 }}>
          <div className="position-absolute top-50 start-50 translate-middle" style={{ width: "min(800px, 95vw)" }}>
            <div className="card shadow-lg">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Add Candidate</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowAdd(false)}>Close</button>
              </div>
              <div className="card-body">
                <form className="row g-3" onSubmit={saveCandidate}>
                  <div className="col-md-6">
                    <label className="form-label">Candidate Name</label>
                    <input className="form-control" value={form.candidateName} onChange={e => setForm(f => ({ ...f, candidateName: e.target.value }))} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Position</label>
                    <input className="form-control" value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Stage</label>
                    <select className="form-select" value={form.stage} onChange={e => setForm(f => ({ ...f, stage: e.target.value }))}>
                      <option>Screening</option>
                      <option>Interview</option>
                      <option>Offer</option>
                      <option>Hired</option>
                      <option>Declined</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Start Date</label>
                    <input type="date" className="form-control" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Source</label>
                    <input className="form-control" value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Contact (email/phone)</label>
                    <input className="form-control" value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Remarks</label>
                    <textarea rows="3" className="form-control" value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                    <button className="btn btn-primary">Save Candidate</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {taskOpen && (
        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: "rgba(0,0,0,0.35)", zIndex: 1050 }}>
          <div className="position-absolute top-50 start-50 translate-middle" style={{ width: "min(900px, 95vw)" }}>
            <div className="card shadow-lg">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Onboarding Tasks — {taskOpen.candidateName}</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setTaskOpen(null)}>Close</button>
              </div>
              <div className="card-body">
                <form className="row g-2 mb-3" onSubmit={addTask}>
                  <div className="col-md-4"><input className="form-control" placeholder="Task" value={taskForm.task} onChange={e => setTaskForm(f => ({ ...f, task: e.target.value }))} required /></div>
                  <div className="col-md-3">
                    <select className="form-select" value={taskForm.owner} onChange={e => setTaskForm(f => ({ ...f, owner: e.target.value }))}>
                      <option>HR</option>
                      <option>IT</option>
                      <option>Admin</option>
                      <option>Finance</option>
                    </select>
                  </div>
                  <div className="col-md-3"><input type="date" className="form-control" value={taskForm.dueDate} onChange={e => setTaskForm(f => ({ ...f, dueDate: e.target.value }))} /></div>
                  <div className="col-md-8"><input className="form-control" placeholder="Notes" value={taskForm.notes} onChange={e => setTaskForm(f => ({ ...f, notes: e.target.value }))} /></div>
                  <div className="col-md-4 d-flex"><button className="btn btn-primary ms-auto">Add Task</button></div>
                </form>
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead className="table-light"><tr><th>Task</th><th>Owner</th><th>Due</th><th>Status</th><th>Notes</th><th></th></tr></thead>
                    <tbody>
                      {tasks.length === 0 ? <tr><td colSpan="6" className="text-center text-muted">No tasks</td></tr> :
                        tasks.map(t => (
                          <tr key={t.id}>
                            <td>{t.task}</td><td>{t.owner}</td><td>{t.dueDate || "-"}</td>
                            <td>{t.status}</td><td>{t.notes || "-"}</td>
                            <td className="text-end d-flex gap-1">
                              <button className="btn btn-sm btn-outline-success" onClick={() => setTaskStatus(t, "Done")}>Done</button>
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => setTaskStatus(t, "Open")}>Open</button>
                            </td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {filesOpen && (
        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: "rgba(0,0,0,0.35)", zIndex: 1050 }}>
          <div className="position-absolute top-50 start-50 translate-middle" style={{ width: "min(800px, 95vw)" }}>
            <div className="card shadow-lg">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Candidate Files — {filesOpen.candidateName}</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setFilesOpen(null)}>Close</button>
              </div>
              <div className="card-body">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <input type="file" className="form-control" onChange={uploadFile} disabled={uploading} />
                  {uploading && <span className="text-muted">Uploading…</span>}
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead className="table-light"><tr><th>Name</th><th>Type</th><th>Uploaded</th><th></th></tr></thead>
                    <tbody>
                      {filesList.length === 0 ? (
                        <tr><td colSpan="4" className="text-center text-muted">No files</td></tr>
                      ) : filesList.map((f, i) => (
                        <tr key={i}>
                          <td><a href={f.url} target="_blank" rel="noreferrer">{f.name}</a></td>
                          <td>{f.type || "-"}</td>
                          <td>{f.uploadedAt ? f.uploadedAt.slice(0,19).replace("T"," ") : "-"}</td>
                          <td className="text-end"><button className="btn btn-sm btn-outline-danger" onClick={() => removeFile(f)}>Remove</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
